import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SystemChip } from '../crm-grid/pills';
import { optionToken, optClasses, OptToken } from '../crm-grid/optionColor';

/**
 * WA-2 conversations-pane rows — CardRow (phone/tablet) + GridRow (desktop table), one data shape and two
 * renderers, exactly as §9 asks of the Leads pair. They share the crm-grid pill primitives (§2.1) so the
 * pane cannot drift into a second chip system.
 *
 * 🔴 DISPLAY-ONLY (INV-1 / AC-W9). Every chip here is a SystemChip: square, no chevron, not a button, no
 * onClick. v1 has no editable pill and no take-over — those are P5. The only interactive element in a row is
 * the row itself (tap → open the thread), so there is no writable path in this file by construction.
 */

export type WaEstado = 'necesita_respuesta' | 'esperando_al_cliente';
export type WaWindowTier = 'none' | 'amber' | 'rose';
// 🔴 `agentId` answers "Míos" / "Sin asignar" for a WEB row, which the console must decide client-side
// (the WhatsApp endpoint resolves owner=mine server-side from the session; web rows never go through it).
// Optional so every existing WhatsApp construction site is unaffected.
export interface WaOwnerChip { kind: 'human' | 'bot_liberada' | 'bot'; name: string | null; agentId?: string | null }

export interface WaRow {
  /** unique row key. WhatsApp rows are keyed by CONVERSATION id (rows are conversations, not people). */
  key: string;
  conversationId: string | null;   // null for a web row (no grid projection)
  visitorId: string;
  contact: string;
  phone: string | null;
  preview: string;
  lastMessageAt: string | null;
  channel: 'whatsapp' | 'web';
  // ── the projection (WhatsApp only; null on a web row, which has no lifecycle observations) ──
  estado: WaEstado | null;
  owner: WaOwnerChip | null;
  waitMs: number | null;
  windowExpiresAt: string | null;
  windowClosing: boolean;
  windowTier: WaWindowTier;
  operationalStatus: string | null;
}

// ── derived display values ─────────────────────────────────────────────────────────────────────────
/** WAIT (AC-W4) — a duration, escalating to amber and STOPPING there. Rose is reserved for the window's
 *  final hour: the wait is uncomfortable, the window is irreversible, and one colour must not say both. */
export function waitLabel(ms: number | null): string {
  if (ms == null) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}
export function waitToken(ms: number | null): OptToken {
  if (ms == null) return 'slate';
  return ms >= 60 * 60 * 1000 ? 'amber' : 'slate'; // amber-max, never rose
}

/** 24h WINDOW — the highest-value pixel in the pane (§6.1). Both the escalation tier (amber <4h, rose <1h)
 *  and the "por cerrar" view predicate (<2h) come from the SERVER off one expiry, with the view's threshold
 *  structurally nested inside amber — so the chip and the view can never contradict (AC-W6b). */
export function windowLabel(expiresAt: string | null, nowMs: number, t: TFunction): string | null {
  if (!expiresAt) return null;                       // no inbound ever → no window to count down
  const left = Date.parse(expiresAt) - nowMs;
  if (left <= 0) return t('waGrid.window.closed');
  const m = Math.floor(left / 60000);
  if (m < 60) return t('waGrid.window.minutesLeft', { count: m });
  return t('waGrid.window.hoursLeft', { count: Math.floor(m / 60) });
}
export function windowToken(tier: WaWindowTier): OptToken {
  // 🔴 NO local threshold. The tier is computed server-side from the same expiry this chip labels, so the
  // ladder cannot drift from the ratified one. It drifted exactly once and this is the fix: the chip was
  // keyed off `windowClosing` (the VIEW's 2h), so a row 3h from closing rendered slate where §6.1 says
  // amber. A threshold the client owns is a threshold that can disagree with the design.
  return tier === 'rose' ? 'rose' : tier === 'amber' ? 'amber' : 'slate';
}

/** OWNER (PRD §1.2) — 4 derived values. `short` is the 375px rendering; the full text is always in `title`
 *  and the accessible label, so "liberada" is never lost, only abbreviated. */
export function ownerLabel(owner: WaOwnerChip | null, t: TFunction): { short: string; full: string } {
  if (!owner) return { short: '—', full: t('waGrid.owner.none') };
  // 🔴 owner.name is USER CONTENT — a real person's name — and is NEVER translated. Only the fallback for
  // an unnamed agent, and the bot states, are chrome. (cmdr's A-scope rule: chrome → t(), content verbatim.)
  if (owner.kind === 'human') { const n = owner.name || t('waGrid.owner.agent'); return { short: n, full: n }; }
  if (owner.kind === 'bot_liberada') return { short: t('waGrid.owner.botReleasedShort'), full: t('waGrid.owner.botReleased') };
  const b = t('waGrid.owner.bot'); return { short: b, full: b };
}

function initials(name: string): string {
  return (name || '·').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '·';
}

/** Channel is a 14px CORNER GLYPH on the avatar, never a chip (§6.1) — it returns a whole pill slot. */
const Avatar: React.FC<{ row: WaRow; size: number }> = ({ row, size }) => {
  const { bg, fg } = optClasses(optionToken(row.visitorId));
  return (
    <span className="relative inline-flex shrink-0">
      <span style={{ width: size, height: size }} className={`inline-flex items-center justify-center rounded-xl text-[11px] font-bold ${bg} ${fg}`}>
        {initials(row.contact)}
      </span>
      <span
        // keeps the P2c-2 name (`channel-badge-whatsapp`): same fact, new shape — the badge moved from an
        // icon in the old list row to the avatar's corner glyph (§6.1), so the assertion it carries survives.
        data-testid={`channel-badge-${row.channel}`}
        aria-label={row.channel === 'whatsapp' ? 'WhatsApp' : 'Web'}
        title={row.channel === 'whatsapp' ? 'WhatsApp' : 'Web'}  /* brand names — identical in every locale */
        // logical inset so the glyph mirrors to the opposite corner in he-RTL (§7)
        className={`absolute -bottom-1 [inset-inline-end:-4px] w-[14px] h-[14px] rounded-full border border-surface flex items-center justify-center text-[8px] font-bold text-white ${row.channel === 'whatsapp' ? 'bg-green-600' : 'bg-primary'}`}
      >
        {row.channel === 'whatsapp' ? 'W' : '•'}
      </span>
    </span>
  );
};

/** ESTADO — 🟡 only *Necesita respuesta* draws a chip (PRD §1.2 render rule). *Esperando al cliente* renders
 *  NOTHING: the non-actionable state is carried by its absence. The projection still HAS a total value —
 *  "renders nothing" is not "is nothing" (AC-W3c), which is why the row carries `data-wa-estado` either way. */
const EstadoChip: React.FC<{ estado: WaEstado | null }> = ({ estado }) => {
  const { t } = useTranslation('common');
  return estado === 'necesita_respuesta'
    ? <SystemChip testId="wa-estado-chip" label={t('waGrid.estado.necesitaRespuesta')} token="amber" />
    : null;
};

const OwnerChip: React.FC<{ owner: WaOwnerChip | null; long?: boolean }> = ({ owner, long }) => {
  const { t } = useTranslation('common');
  const { short, full } = ownerLabel(owner, t);
  return <SystemChip testId="wa-owner-chip" label={long ? full : short} title={full} tone="muted" />;
};

/** WAIT cell — text, not a chip: it is a magnitude, and the pane already spends its chip slots. Colour
 *  escalates to amber and stops (see waitToken). */
const WaitCell: React.FC<{ ms: number | null; className?: string }> = ({ ms, className }) => (
  <span data-testid="wa-wait" dir="ltr" className={`${className ?? ''} ${optClasses(waitToken(ms)).fg} ${ms != null && waitToken(ms) === 'amber' ? 'font-semibold' : ''}`}>
    {waitLabel(ms)}
  </span>
);

const WindowChip: React.FC<{ row: WaRow; nowMs: number }> = ({ row, nowMs }) => {
  const { t } = useTranslation('common');
  const label = windowLabel(row.windowExpiresAt, nowMs, t);
  if (!label) return null;
  return <SystemChip testId="wa-window-chip" label={label} token={windowToken(row.windowTier)} />;
};

// ── CardRow (phone/tablet) ─────────────────────────────────────────────────────────────────────────
export const WaCardRow: React.FC<{ row: WaRow; selected?: boolean; nowMs: number; onOpen: (row: WaRow) => void }> = ({ row, selected, nowMs, onOpen }) => (
  <div
    role="button"
    tabIndex={0}
    // `chat-item-<visitorId>` is the console's long-standing row identity (several ratified specs select on
    // it); `data-wa-row` is the CONVERSATION identity this grid adds, because rows are conversations and a
    // contact can have more than one (AC-W10).
    data-testid={`chat-item-${row.visitorId}`}
    data-wa-row={row.key}
    data-wa-estado={row.estado ?? 'none'}
    data-wa-owner={row.owner?.kind ?? 'none'}
    onClick={() => onOpen(row)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(row); } }}
    className={`flex gap-3 p-3 rounded-xl bg-surface border cursor-pointer ${selected ? 'ring-2 ring-primary/15 border-primary/30' : 'border-border-neutral'}`}
  >
    <Avatar row={row} size={38} />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-strong truncate">{row.contact}</span>
        <WaitCell ms={row.waitMs} className="ms-auto text-[11px] shrink-0" />
      </div>
      <div className="text-xs text-text-muted truncate">{row.preview || '—'}</div>
      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <EstadoChip estado={row.estado} />
        <OwnerChip owner={row.owner} />
        <WindowChip row={row} nowMs={nowMs} />
      </div>
    </div>
  </div>
);

// ── GridRow (desktop table) ────────────────────────────────────────────────────────────────────────
export const WaGridRow: React.FC<{ row: WaRow; selected?: boolean; nowMs: number; onOpen: (row: WaRow) => void }> = ({ row, selected, nowMs, onOpen }) => (
  <tr
    data-testid={`chat-item-${row.visitorId}`}
    data-wa-row={row.key}
    data-wa-estado={row.estado ?? 'none'}
    data-wa-owner={row.owner?.kind ?? 'none'}
    onClick={() => onOpen(row)}
    className={`border-b border-border-neutral hover:bg-canvas cursor-pointer ${selected ? 'bg-canvas' : ''}`}
  >
    {/* sticky identity column — inset-inline-start so it sticks to the correct edge in he-RTL (§7) */}
    <td className="sticky z-10 bg-surface p-4 [inset-inline-start:0]">
      <div className="flex items-center gap-2 min-w-0">
        <Avatar row={row} size={28} />
        <span className="text-sm font-semibold text-text-strong truncate">{row.contact}</span>
      </div>
    </td>
    <td className="p-4 text-xs text-text-muted whitespace-nowrap" dir="ltr">{row.phone || '—'}</td>
    <td className="p-4 text-xs text-text-muted max-w-[24rem] truncate">{row.preview || '—'}</td>
    <td className="p-4 whitespace-nowrap"><EstadoChip estado={row.estado} /></td>
    <td className="p-4 whitespace-nowrap"><OwnerChip owner={row.owner} long /></td>
    <td className="p-4 whitespace-nowrap text-[11px]"><WaitCell ms={row.waitMs} /></td>
    <td className="p-4 whitespace-nowrap"><WindowChip row={row} nowMs={nowMs} /></td>
    <td className="p-4 whitespace-nowrap">
      {row.operationalStatus ? <SystemChip testId="wa-operational-chip" label={row.operationalStatus} tone="muted" /> : null}
    </td>
  </tr>
);
